import { useMemo } from "react";

import { Dropdown, EllipsisDropdownItem, PlainTooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { TOPIC_HEADER_ID } from "app/pages/activation/components/Topics/Dropdown/config";
import type { KWWithFlagSchema } from "app/pages/activation/hooks/useKeywordsGroups";
import { activationTracking } from "app/pages/activation/services/tracking";

import { SeparatorItem } from "../../../Shared/SeparatorItem/SeparatorItem";
import { keywordModalTexts } from "../../Modal/Flow/Advanced/texts";
import { KEYWORD_GROUP_ALREADY_USED, KEYWORD_GROUP_APPLY } from "../../texts";
import { getDropdownConfig, SEPARATOR_ITEM_FIRST, SEPARATOR_ITEM_SECOND } from "../constants";

import * as SC from "./styles";
import { TOOLTIP_TEXT_REACH_LIMIT } from "./texts";
import type { DropdownListSchema } from "./types";

const DropdownList = ({
    keywordsGroups,
    isMaxSelected,
    isMaxCreated,
    selectedKWGroupIds,
    dropdownRef,
    isTopicsLoading,
    isSomeItemSelected,
    onSelect,
    onApply,
}: DropdownListSchema) => {
    const track = activationTracking.home;

    const getItems = (KWGroupList: KWWithFlagSchema[]) =>
        KWGroupList
            ? KWGroupList?.map((item) => {
                  const isSelected = selectedKWGroupIds[item.id];
                  const isDisabled = (isMaxSelected && !isSelected) || item.isAlreadyUsed;

                  return (
                      <EllipsisDropdownItem
                          iconName="nav-keyword-group"
                          iconSize="sm"
                          key={item.id}
                          id={item.id}
                          selected={isSelected}
                          disabled={isDisabled}
                          tooltipText={item.isAlreadyUsed ? translate(KEYWORD_GROUP_ALREADY_USED) : null}
                          showCheckBox={!isDisabled}
                      >
                          {item.text}
                      </EllipsisDropdownItem>
                  );
              })
            : [];

    const applyButton = useMemo(
        () =>
            isSomeItemSelected
                ? () => (
                      <SC.ApplyButton onClick={onApply}>
                          <PlainTooltip
                              appendTo="body"
                              defaultOpen={isMaxSelected}
                              enabled={isMaxSelected}
                              tooltipContent={translate(TOOLTIP_TEXT_REACH_LIMIT)}
                          >
                              <span>{translate(KEYWORD_GROUP_APPLY)}</span>
                          </PlainTooltip>
                      </SC.ApplyButton>
                  )
                : null,
        [isSomeItemSelected, isMaxSelected],
    );

    const onToggle = (isOpen: boolean) => {
        if (isOpen) {
            track.trackLinkKWListOpen();
        }
    };

    const ownKWList = useMemo(() => getItems(keywordsGroups.ownKWList), [isMaxSelected, keywordsGroups]);
    const sharedKWList = useMemo(() => getItems(keywordsGroups.sharedKWList), [isMaxSelected, keywordsGroups]);

    return (
        <Dropdown
            hasSearch
            ref={dropdownRef}
            disabled={isTopicsLoading || isMaxCreated}
            width={getDropdownConfig().width}
            dropdownPopupHeight={getDropdownConfig().dropdownPopupHeight}
            searchPlaceHolder={getDropdownConfig().searchPlaceholder}
            closeOnItemClick={false}
            appendTo={`#${TOPIC_HEADER_ID}`}
            dropdownPopupPlacement="ontop-left"
            buttonWidth="auto"
            onClick={onSelect}
            selectedIds={selectedKWGroupIds}
            footerComponent={applyButton}
            onToggle={onToggle}
        >
            {[
                <SC.PixelButton />,

                <SeparatorItem
                    key={SEPARATOR_ITEM_FIRST}
                    isVisible={!!keywordsGroups?.ownKWList?.length}
                    text={translate(keywordModalTexts.dropdownSeparatorMyListText)}
                />,
                ...ownKWList,

                <SeparatorItem
                    key={SEPARATOR_ITEM_SECOND}
                    isVisible={!!keywordsGroups?.sharedKWList?.length}
                    text={translate(keywordModalTexts.dropdownSeparatorSharedListText)}
                />,
                ...sharedKWList,
            ]}
        </Dropdown>
    );
};

export default DropdownList;
